import axios from 'axios';
import React, { useState } from 'react';

const MigrationTool = () => {
    const [tenantId, setTenantId] = useState('lima-123');
    const [migrationType, setMigrationType] = useState('full'); // 'full', 'tenantId-only', or 'imageUrls'
    const [isRunning, setIsRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleMigration = async () => {
        if (!tenantId.trim()) {
            setError('Please enter a tenant ID');
            return;
        }

        setIsRunning(true);
        setError(null);
        setResult(null);

        try {
            let endpoint;
            if (migrationType === 'full') {
                endpoint = '/api/migration/run';
            } else if (migrationType === 'tenantId-only') {
                endpoint = '/api/migration/add-tenant-id';
            } else if (migrationType === 'imageUrls') {
                endpoint = '/api/migration/update-image-urls';
            }

            const response = await axios.post(endpoint, {
                tenantId: tenantId.trim()
            });

            setResult(response.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Migration failed');
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4">
                <div className="bg-white rounded-lg shadow-lg p-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-6">
                        Multi-Tenancy Migration Tool
                    </h1>

                    <div className="mb-6">
                        <p className="text-gray-600 mb-4">
                            This tool will migrate all existing data to include tenantId fields and update image URLs
                            to include the tenant ID in the path. The migrated data will be saved to a new database
                            called 'migrateddata'.
                        </p>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">
                                        Important Warning
                                    </h3>
                                    <div className="mt-2 text-sm text-yellow-700">
                                        <p>
                                            This migration will modify all existing data. Make sure you have a backup of your
                                            database before proceeding. The migration will:
                                        </p>
                                        <ul className="list-disc list-inside mt-2">
                                            <li>Add tenantId field to all records</li>
                                            <li>Update all image URLs to include tenantId in the path</li>
                                            <li>Save results to a new 'migrateddata' database</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <label htmlFor="migrationType" className="block text-sm font-medium text-gray-700 mb-2">
                            Migration Type
                        </label>
                        <select
                            id="migrationType"
                            value={migrationType}
                            onChange={(e) => setMigrationType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="full">Full Migration (Add tenantId + Update Image URLs)</option>
                            <option value="tenantId-only">TenantId Only (Add tenantId field only)</option>
                            <option value="imageUrls">Image URLs Only (Update all image URLs)</option>
                        </select>
                        <p className="mt-1 text-sm text-gray-500">
                            Choose the type of migration you want to perform
                        </p>
                    </div>

                    <div className="mb-6">
                        <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700 mb-2">
                            Tenant ID
                        </label>
                        <input
                            type="text"
                            id="tenantId"
                            value={tenantId}
                            onChange={(e) => setTenantId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Enter tenant ID (e.g., 'lima-123', 'tenant1', etc.)"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                            This will be used as the tenantId for all existing records
                        </p>
                    </div>

                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleMigration}
                            disabled={isRunning}
                            className={`px-6 py-3 rounded-md font-medium text-white ${isRunning
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                }`}
                        >
                            {isRunning ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Running {migrationType === 'full' ? 'Full Migration' : migrationType === 'tenantId-only' ? 'TenantId Migration' : 'Image URL Migration'}...
                                </div>
                            ) : (
                                `Start ${migrationType === 'full' ? 'Full' : migrationType === 'tenantId-only' ? 'TenantId-Only' : 'Image URL'} Migration`
                            )}
                        </button>
                    </div>

                    {error && (
                        <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">
                                        Migration Error
                                    </h3>
                                    <div className="mt-2 text-sm text-red-700">
                                        <p>{error}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {result && (
                        <div className="mt-6 bg-green-50 border border-green-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-green-800">
                                        Migration Completed Successfully
                                    </h3>
                                    <div className="mt-2 text-sm text-green-700">
                                        <p><strong>Tenant ID:</strong> {result.tenantId}</p>
                                        <p><strong>Total Migrated:</strong> {result.totalMigrated} documents</p>
                                        <p><strong>Total Errors:</strong> {result.totalErrors}</p>
                                        <p><strong>Completed at:</strong> {new Date(result.timestamp).toLocaleString()}</p>
                                    </div>

                                    {result.results && (
                                        <div className="mt-4">
                                            <h4 className="text-sm font-medium text-green-800 mb-2">Detailed Results:</h4>
                                            <div className="bg-white rounded border p-3 max-h-60 overflow-y-auto">
                                                {Object.entries(result.results).map(([modelName, modelResult]) => (
                                                    <div key={modelName} className="text-xs">
                                                        <span className="font-medium">{modelName}:</span> {modelResult.migrated} migrated, {modelResult.errors} errors
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MigrationTool; 